/**
 * Canonical pack ids, template/UI mappings, and Phase 1 capability ids.
 * Single source of truth for vendor-pack Phase 1 enabling layer.
 */

import type { CapabilityId, PackId } from '../../types/vendorPacks.ts';

export const PACK_ID_CORE_ITSM = 'core-itsm' as const satisfies PackId;
export const PACK_ID_ATLASSIAN_JSM = 'atlassian-jsm' as const satisfies PackId;

/** Legacy firms (missing or empty enabledVendorPackIds) get both packs, in stable order. */
export const DEFAULT_LEGACY_ENABLED_PACK_IDS: readonly PackId[] = [
  PACK_ID_CORE_ITSM,
  PACK_ID_ATLASSIAN_JSM,
];

/** Phase 1 only — do not extend until a feature is gated (Phase 2+). */
export const PHASE1_CAPABILITY_IDS: readonly CapabilityId[] = [
  'pathkit.core.discovery',
  'pathkit.core.insights',
  'pathkit.atlassian.ui',
  'pathkit.atlassian.integration',
  'pathkit.atlassian.education',
];

/** Phase 3B: backend enforcement for Jira / Forge / AI JQL integration routes. */
export const CAPABILITY_ATLASSIAN_INTEGRATION =
  'pathkit.atlassian.integration' as const satisfies CapabilityId;

/**
 * Phase 3B: backend enforcement for Atlassian UI surfaces (Spaces / Space Discovery /
 * Space Planning). Distinct from the integration cap because these routes do not call
 * the Jira API — they manage Atlassian-shaped planning state inside PathKit's KV.
 *
 * Mirrors the value in `hooks/useFirmCapabilities.ts` (`CAPABILITY_ATLASSIAN_UI`); both
 * resolve to the same pack `provides[]` entry.
 */
export const CAPABILITY_ATLASSIAN_UI =
  'pathkit.atlassian.ui' as const satisfies CapabilityId;

/** Server engagement / vendor template keys (e.g. vendorTemplates.ts) → pack id. */
export const TEMPLATE_KEY_TO_PACK_ID: Readonly<Record<string, PackId>> = {
  'atlassian-jsm': PACK_ID_ATLASSIAN_JSM,
  servicenow: 'servicenow',
  zendesk: 'zendesk',
};

/** UI TemplateType / company templateType → primary vendor pack id (core for generic). */
export const UI_TEMPLATE_TYPE_TO_PACK_ID: Readonly<Record<string, PackId>> = {
  atlassian: PACK_ID_ATLASSIAN_JSM,
  servicenow: 'servicenow',
  ivanti: 'ivanti',
  'bmc-helix': 'bmc-helix',
  freshservice: 'freshservice',
  custom: PACK_ID_CORE_ITSM,
};

/**
 * Legacy alias strings (UI, BDM extractions, onboarding) → canonical vendor `templateKey`
 * used with {@link TEMPLATE_KEY_TO_PACK_ID} / registry. Prefer `atlassian-jsm` over `atlassian`.
 */
export const CANONICAL_TEMPLATE_KEY_ALIASES: Readonly<Record<string, string>> = {
  atlassian: 'atlassian-jsm',
  jira: 'atlassian-jsm',
  jsm: 'atlassian-jsm',
  atlassian_jsm: 'atlassian-jsm',
};

/** Normalize free-form keys to canonical template keys; unknown keys returned lowercased trimmed. */
export function normalizeTemplateKeyToCanonical(key: string | null | undefined): string {
  if (key == null) return '';
  const t = String(key).trim().toLowerCase();
  return CANONICAL_TEMPLATE_KEY_ALIASES[t] ?? t;
}
